/** Cognito Hosted UI authorization-code flow with PKCE. The API verifies tokens. */
const TOKEN_KEY = 'bookkeeper.auth.tokens'
export const REQUIRED_GROUP = 'sosw-admin'
const LOGIN_REDIRECT_KEY = 'bookkeeper.auth.lastRedirect'
export const LOGIN_BLOCKED_EVENT = 'bookkeeper.auth.loginBlocked'
const TRANSACTION_KEY = 'bookkeeper.auth.transaction'

interface Tokens {
  id_token: string
  access_token?: string
  refresh_token?: string
}
interface Transaction {
  state: string
  verifier: string
  returnTo: string
}

export function authConfigured(): boolean {
  return Boolean(import.meta.env.VITE_COGNITO_DOMAIN && import.meta.env.VITE_COGNITO_CLIENT_ID)
}

function config() {
  if (!authConfigured()) throw new Error('Sign-in is not configured')
  return {
    domain: import.meta.env.VITE_COGNITO_DOMAIN.replace(/\/$/, ''),
    clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
    redirectUri: `${window.location.origin}/auth/callback`,
  }
}

function base64url(bytes: Uint8Array): string {
  return btoa(String.fromCharCode(...bytes)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')
}

function random(): string {
  return base64url(crypto.getRandomValues(new Uint8Array(32)))
}

export async function pkceChallenge(verifier: string): Promise<string> {
  return base64url(new Uint8Array(await crypto.subtle.digest('SHA-256', new TextEncoder().encode(verifier))))
}

export function idTokenClaims(token: string): { exp?: number; email?: string; 'cognito:groups'?: string[] } {
  try {
    const payload = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
    return JSON.parse(atob(payload))
  } catch {
    return {}
  }
}

function readTokens(): Tokens | null {
  try {
    const tokens = JSON.parse(localStorage.getItem(TOKEN_KEY) || 'null')
    return typeof tokens?.id_token === 'string' ? tokens : null
  } catch {
    return null
  }
}

function usable(token: string): boolean {
  const { exp } = idTokenClaims(token)
  return typeof exp === 'number' && exp > Date.now() / 1000 + 30
}

let loginInFlight: Promise<void> | null = null
let refreshInFlight: Promise<string | null> | null = null
// Prevent a refresh that finishes after sign-out from restoring a session.
let generation = 0

export function clearSession(): void {
  generation++
  localStorage.removeItem(TOKEN_KEY)
  refreshInFlight = null
}

// A restored page must be able to start a new flow; the tab guard still applies.
window.addEventListener('pageshow', event => {
  if (event.persisted) loginInFlight = null
})

export function requireLogin(): Promise<void> {
  if (!loginInFlight) clearSession()
  return login()
}

export function login(manual = false): Promise<void> {
  if (loginInFlight) return loginInFlight
  loginInFlight = (async () => {
    const lastRedirect = Number(sessionStorage.getItem(LOGIN_REDIRECT_KEY))
    if (!manual && lastRedirect && Date.now() - lastRedirect < 60_000) {
      window.dispatchEvent(new Event(LOGIN_BLOCKED_EVENT))
      throw new Error('Automatic sign-in paused. Please sign in again.')
    }
    const { domain, clientId, redirectUri } = config()
    const state = random()
    const verifier = random()
    const returnTo = window.location.pathname === '/auth/callback'
      ? '/' : window.location.pathname + window.location.search + window.location.hash
    sessionStorage.setItem(TRANSACTION_KEY, JSON.stringify({ state, verifier, returnTo }))
    const query = new URLSearchParams({
      response_type: 'code', client_id: clientId, redirect_uri: redirectUri,
      scope: 'openid email profile', state, code_challenge: await pkceChallenge(verifier),
      code_challenge_method: 'S256',
    })
    sessionStorage.setItem(LOGIN_REDIRECT_KEY, String(Date.now()))
    window.location.assign(`${domain}/oauth2/authorize?${query}`)
  })().catch(error => {
    loginInFlight = null
    throw error
  })
  return loginInFlight
}

async function exchange(params: Record<string, string>): Promise<Tokens> {
  const { domain, clientId } = config()
  const response = await fetch(`${domain}/oauth2/token`, {
    method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ ...params, client_id: clientId }),
  })
  if (!response.ok) throw new Error('Sign-in failed. Please sign in again.')
  const tokens = await response.json() as Tokens
  if (typeof tokens.id_token !== 'string' || !usable(tokens.id_token)) {
    throw new Error('Sign-in returned an invalid session')
  }
  return tokens
}

export async function handleCallback(): Promise<string> {
  const transaction = sessionStorage.getItem(TRANSACTION_KEY)
  sessionStorage.removeItem(TRANSACTION_KEY) // Single-use, including failed exchanges.
  const params = new URLSearchParams(window.location.search)
  const code = params.get('code')
  loginInFlight = null
  let saved: Transaction | null = null
  try {
    saved = transaction ? JSON.parse(transaction) : null
  } catch {
    throw new Error('Sign-in state mismatch. Please sign in again.')
  }
  if (!saved || !params.get('state') || params.get('state') !== saved.state || !code || !saved.verifier) {
    throw new Error('Sign-in state mismatch. Please sign in again.')
  }
  const currentGeneration = generation
  const tokens = await exchange({ grant_type: 'authorization_code', code,
    redirect_uri: config().redirectUri, code_verifier: saved.verifier })
  if (generation !== currentGeneration) throw new Error('Session was cleared')
  localStorage.setItem(TOKEN_KEY, JSON.stringify(tokens))
  // Only permit local paths; sessionStorage is not an open redirect destination.
  return saved.returnTo.startsWith('/') && !saved.returnTo.startsWith('//') && !saved.returnTo.includes('\\')
    ? saved.returnTo : '/'
}

export async function getIdToken(forceRefresh = false): Promise<string | null> {
  if (!authConfigured()) return null
  if (refreshInFlight) return refreshInFlight
  const tokens = readTokens()
  if (!tokens) return null
  if (!forceRefresh && usable(tokens.id_token)) return tokens.id_token
  if (!tokens.refresh_token) {
    clearSession()
    return null
  }
  const currentGeneration = generation
  const pending = (async () => {
    try {
      const updated = await exchange({ grant_type: 'refresh_token', refresh_token: tokens.refresh_token! })
      if (generation !== currentGeneration) return null
      localStorage.setItem(TOKEN_KEY, JSON.stringify({ ...tokens, ...updated }))
      return updated.id_token
    } catch {
      if (generation === currentGeneration) clearSession()
      return null
    } finally {
      if (generation === currentGeneration) refreshInFlight = null
    }
  })()
  refreshInFlight = pending
  return pending
}

export function signedInEmail(): string {
  const tokens = readTokens()
  return tokens ? idTokenClaims(tokens.id_token).email || '' : ''
}

export async function logout(): Promise<void> {
  const { domain, clientId } = config()
  const refreshToken = readTokens()?.refresh_token
  // Invalidate pending refreshes before attempting public-client revocation.
  generation++
  refreshInFlight = null
  if (refreshToken) {
    const controller = new AbortController()
    const timeout = setTimeout(() => controller.abort(), 2000)
    try {
      await fetch(`${domain}/oauth2/revoke`, {
        method: 'POST', headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({ token: refreshToken, client_id: clientId }),
        signal: controller.signal,
      })
    } catch {
      // Revocation is best-effort; Hosted UI sign-out must still happen.
    } finally {
      clearTimeout(timeout)
    }
  }
  clearSession()
  sessionStorage.removeItem(TRANSACTION_KEY)
  loginInFlight = null
  const query = new URLSearchParams({ client_id: clientId, logout_uri: `${window.location.origin}/` })
  window.location.assign(`${domain}/logout?${query}`)
}
